<template>
  <div class="w-full flex items-center justify-center gap-2">
    <UseExample :src="exampleSrc" />
    <div class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4">
      <div class="inline-block">
        <div class="flex flex-wrap gap-1.5 p-2">
          <Button
            v-for="key in mediaKeys"
            :key="key.keywords"
            color="normal"
            class="h-10"
            draggable="true"
            @click="() => handleClick(key)"
            @dragstart="(event: DragEvent) => handleDrag(key, event)"
            @drag="onDrag"
            @dragend="(event: DragEvent) => handleDragEnd(key, event)"
          >
            <Icon :icon="key.icon" />
            {{ $t(key.labelKey) }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { Button, Icon } from '@rapoo/element'
  import UseExample from '@/components/UseExample.vue'
  import { setDragData, DragType, createMicroDragClickFallback } from '@/utils/dnd'
  import { KEYCODE_KEYWORDS_MAP } from '@/views/device/mouse/keys/utils/keyMapSetting'
  import { useLocaleStore } from '@/stores'
  import { useDragImage } from '@/views/device/keyboard/keys/composables/useDragImage'
  import { MEDIA_KEYS } from '@/views/device/mouse/keys/utils/constants'
  import type { MediaKey } from '@/views/device/mouse/keys/utils/constants'
  import {
    SelectedKeySymbol,
    UpdateSelectedKeyMappingSymbol,
  } from '@/views/device/mouse/keys/types'
  import type { KeyChangePayload, KeyType, UpdateSelectedKeyMapping } from '../types'
  import { DISABLED_KEYWORDS } from '@/utils/platformDisable'
  import useExampleZhCN from '@/assets/images/recording/mouse/media-zhCN.gif'
  import useExampleEnUS from '@/assets/images/recording/mouse/media-enUS.gif'

  const mediaKeys = MEDIA_KEYS.filter((k) => !DISABLED_KEYWORDS.has(k.keywords))

  const { t, language } = useLocaleStore()
  const { setDragImage } = useDragImage()
  const { onDragStart, onDrag, shouldTreatAsClick } = createMicroDragClickFallback()

  const exampleSrc = computed(() => (language === 'zh-CN' ? useExampleZhCN : useExampleEnUS))

  const selectedKey = inject(SelectedKeySymbol)
  const updateSelectedKeyMapping = inject<UpdateSelectedKeyMapping>(UpdateSelectedKeyMappingSymbol)

  /**
   * 根据 keycode 元数据中的 type 字段推断改键类型。
   * type === 0x01 为键盘键，其余归为多媒体键。
   */
  const resolveKeyType = (type: unknown): KeyType => {
    return Number(type) === 0x01 ? 'keyboard' : 'media'
  }

  /**
   * 构建改键 payload：
   * 1. 通过 keywords 在 KEYCODE_KEYWORDS_MAP 中查找目标键的元数据；
   * 2. 组装 KeyChangePayload，包含当前选中按键的 keywords、目标键类型、目标元数据和图标；
   * 3. 使用 t(labelKey) 生成本地化 label 存入 raw.originalLabel，
   *    供鼠标布局面板显示改键后的可读名称。
   *
   * @param key 当前点击/拖拽的多媒体按键配置
   * @returns 改键 payload；若 keywords 未在元数据中注册则返回 undefined
   */
  const buildKeyChangePayload = (key: MediaKey): KeyChangePayload | undefined => {
    const target = KEYCODE_KEYWORDS_MAP.get(key.keywords)
    if (!target) return undefined

    return {
      keywords: selectedKey?.value?.keywords ?? '',
      keyType: resolveKeyType(target.type),
      target: target as KeyChangePayload['target'],
      raw: {
        ...target,
        icon: key.icon,
        originalLabel: t(key.labelKey),
      },
    }
  }

  /**
   * 点击改键：将当前选中的物理按键映射为点击的多媒体功能键。
   * 前置条件：必须已选中一个物理按键（selectedKey）且注入了更新方法。
   */
  const handleClick = (key: MediaKey) => {
    if (!selectedKey?.value || !updateSelectedKeyMapping) return

    const payload = buildKeyChangePayload(key)
    if (!payload) return

    updateSelectedKeyMapping(payload)
  }

  /**
   * 拖拽改键：将多媒体功能键的改键数据写入 dataTransfer，
   * 拖放到目标物理按键区域后由目标组件完成映射。
   */
  const handleDrag = (key: MediaKey, e: DragEvent) => {
    const dataTransfer = e.dataTransfer
    if (!dataTransfer) return

    const dragData = buildKeyChangePayload(key)
    if (!dragData) return

    setDragData(dataTransfer, DragType.KEY_MAPPING, dragData)
    setDragImage(e, {
      icon: key.icon,
    })
    onDragStart(e)
  }

  /**
   * 微拖拽兜底：点击时手部微动会被浏览器误判为拖拽并吞掉 click，
   * 若本次拖拽未落到任何目标且位移极小，则按点击改键处理。
   */
  const handleDragEnd = (key: MediaKey, e: DragEvent) => {
    if (shouldTreatAsClick(e)) {
      handleClick(key)
    }
  }
</script>

<style scoped></style>
